import { LIVE_SNAPSHOT_ID, KEY_SEPARATOR, REPORT_KEYS } from '../common/constants';
import { getDisplayName } from '../../../utils/components';
import {
  getUniqueCompositeKeys,
  deriveHeadingDisplayKey,
  addCompositeKeys,
  addCompositeKeysToSummaryMap,
  concatWithCompositeKey,
} from './composite_keys';
import { formatDate } from '../../../utils/date-utils';
import { GOAL_KEY, COMPONENT_GRAPH_ACCURACY_OPTION_KEYS } from '../../../constants/trend_report';

export const accuracyDataWithCompositeKeys = (responsePayloads, compositeKeys, mergeKeys) => {
  let accuracyListByCompositeKey = {};
  for (let i = 0; i < responsePayloads.length; i++) {
    const compositeKey = compositeKeys[i];
    const { sortedKeys, accuracyList } = responsePayloads[i];
    const accuracyListWithCompositeKeys = addCompositeKeys(accuracyList, sortedKeys, compositeKey);
    accuracyListByCompositeKey[compositeKey] = (accuracyListByCompositeKey[compositeKey] || []).concat(
      accuracyListWithCompositeKeys
    );
  }

  return mergeAccuracyItems(accuracyListByCompositeKey, mergeKeys);
};

const mergeAccuracyItems = (accuracyListByCompositeKey, mergeKeys) => {
  const accuracyItemsByMergeKey = Object.keys(accuracyListByCompositeKey).reduce((accumulator, compositeKey) => {
    const accuracyItems = accuracyListByCompositeKey[compositeKey];
    const mergeKeysWithCompositeKeys = mergeKeys.map((k) => `${compositeKey}${KEY_SEPARATOR}${k}`);

    const mergeMap = accuracyItems.reduce((acc, item) => {
      const mergeKeyValue = mergeKeysWithCompositeKeys.map((k) => item[k]).join(KEY_SEPARATOR);
      acc[mergeKeyValue] = (acc[mergeKeyValue] || []).concat(item);
      return acc;
    }, {});

    Object.keys(mergeMap).forEach((k) => {
      accumulator[k] = (accumulator[k] || []).concat(mergeMap[k]);
    });

    return accumulator;
  }, {});

  return Object.keys(accuracyItemsByMergeKey).reduce((accumulator, key) => {
    const accuracyItems = accuracyItemsByMergeKey[key];
    const mergedItem = accuracyItems.reduce((acc, item) => {
      acc = { ...acc, ...item };
      return acc;
    }, {});

    return accumulator.concat(mergedItem);
  }, []);
};

export const summaryDataWithCompositeKeys = (responsePayloads, compositeKeys) => {
  let summaryData = [];
  for (let i = 0; i < responsePayloads.length; i++) {
    const { summaryMap } = responsePayloads[i];

    const summaryMapWithCompositeKeys = addCompositeKeysToSummaryMap(summaryMap, compositeKeys[i]);
    summaryData.push(...summaryMapWithCompositeKeys);
  }

  return summaryData.reduce((acc, item) => {
    Object.keys(item).forEach((k) => {
      acc[k] = { ...(acc[k] || {}), ...item[k] };
    });
    return acc;
  }, {});
};

export const transformToReportFormat = (
  sortedKeysWithCompositeKeys,
  sortedHeadingsWithCompositeKeys,
  accuracyDataWithCompositeKeys,
  dedupedColKeys
) => {
  let processedKeys = {};

  let reportKeys = [];
  let reportHeadings = [];
  let reportRows = [];

  const uniqueCompositeKeys = getUniqueCompositeKeys(sortedKeysWithCompositeKeys);
  const singleDataSet = uniqueCompositeKeys.length === 1;

  for (let i = 0; i < sortedKeysWithCompositeKeys.length; ++i) {
    const compositeKey = sortedKeysWithCompositeKeys[i];
    const keyParts = compositeKey.split(KEY_SEPARATOR);
    const key = dedupedColKeys.includes(keyParts[1]) ? keyParts[1] : compositeKey;

    if (processedKeys[key]) {
      continue;
    }

    reportKeys.push(key);
    processedKeys[key] = true;

    const compositeHeading = sortedHeadingsWithCompositeKeys[i];
    const headingParts = compositeHeading.split(KEY_SEPARATOR);
    const heading =
      dedupedColKeys.includes(keyParts[1]) || singleDataSet
        ? headingParts[1]
        : `${deriveHeadingDisplayKey(headingParts[0])}${headingParts[1]}`;

    reportHeadings.push(heading);

    for (let j = 0; j < accuracyDataWithCompositeKeys.length; ++j) {
      const row = accuracyDataWithCompositeKeys[j];
      const value = key === REPORT_KEYS.COMPONENT ? getDisplayName(row[compositeKey]) : row[compositeKey];
      const oldValue = reportRows[j] ? reportRows[j][key] : null;
      const updatedValue = value || oldValue || 'N/A';
      reportRows[j] = { ...(reportRows[j] || {}), [key]: updatedValue };
    }
  }

  return { reportKeys, reportHeadings, reportRows };
};

export const replaceLiveSnapshotId = (requestPayloads, liveSnapshot) => {
  return requestPayloads.map((p) => {
    return p.snapshot.id === liveSnapshot.id ? { ...p, snapshot: { ...p.snapshot, id: LIVE_SNAPSHOT_ID } } : p;
  });
};

export const prepareSnapshotName = (snapshot, liveSnapshotId) => {
  return snapshot.id === liveSnapshotId ? LIVE_SNAPSHOT_ID : snapshot.name;
};

export const trendDataWithCompositeKeys = (responsePayloads, compositeKeys, reformatRecordData, getTrendGoal) => {
  let processedSummaryData = {};
  let processedComponentData = {};
  let allChannels = [];
  let allComponents = [];
  let sortedKeys = [];
  let sortedHeadings = [];
  let sortedMonths = [];
  for (let i = 0; i < responsePayloads.length; i++) {
    sortedKeys = responsePayloads[i].sortedKeys;
    sortedHeadings = responsePayloads[i].sortedHeadings;
    sortedMonths = responsePayloads[i].sortedMonths;
    let compositeKey = compositeKeys[i];
    for (let monthIndex = 0; monthIndex < sortedMonths.length; monthIndex++) {
      let month = sortedMonths[monthIndex];
      if (!processedSummaryData.hasOwnProperty(month)) {
        processedSummaryData[month] = {
          formattedMonth: formatDate(month),
          month: month,
        };
      }
      if (!processedComponentData.hasOwnProperty(month)) {
        processedComponentData[month] = {
          formattedMonth: formatDate(month),
          month: month,
        };
      }
      let monthlySummaryTrend = responsePayloads[i].summaryTrend[monthIndex];
      Object.keys(monthlySummaryTrend).map((channelKey) => {
        Object.keys(monthlySummaryTrend[channelKey]).map((answerType) => {
          Object.keys(monthlySummaryTrend[channelKey][answerType]).map((accuracyType) => {
            let channel = channelKey.replace('ChannelSummaryAccuracy', '').toUpperCase();
            let accuracyTypeKey = concatWithCompositeKey([channel, answerType, accuracyType].join('-'), compositeKey);
            processedSummaryData[month][accuracyTypeKey] = reformatRecordData(
              monthlySummaryTrend[channelKey][answerType][accuracyType]
            );
            if (answerType.includes('firstCpAvAccuracy')) {
              accuracyTypeKey = [accuracyTypeKey, GOAL_KEY].join('-');
              processedSummaryData[month][accuracyTypeKey] = reformatRecordData(getTrendGoal(channel));
            }
          });
        });
      });

      let monthlyAccuracy = responsePayloads[i].accuracyTrend[monthIndex];
      monthlyAccuracy.forEach((componentAccuracyRecord) => {
        let channelName = componentAccuracyRecord.channel;
        if (!allChannels.includes(channelName)) {
          allChannels.push(channelName);
        }
        let componentName = componentAccuracyRecord.component;
        if (!allComponents.includes(componentName)) {
          allComponents.push(componentName);
        }
        Object.keys(componentAccuracyRecord).map((recordKey) => {
          if (COMPONENT_GRAPH_ACCURACY_OPTION_KEYS.includes(recordKey)) {
            let key = concatWithCompositeKey([channelName, recordKey, componentName].join('-'), compositeKey);
            processedComponentData[month][key] = reformatRecordData(componentAccuracyRecord[recordKey]);
            if (recordKey.includes('dailyCPAccuracyAV')) {
              key = [key, GOAL_KEY].join('-');
              processedComponentData[month][key] = reformatRecordData(getTrendGoal(componentName.replace(/\s/g, '')));
            }
            if (componentName === 'Contribution Profit' && recordKey === 'perUnitNetVarianceLastCP') {
              key = concatWithCompositeKey(`${channelName}-perUnitVariance`, compositeKey);
              processedSummaryData[month][key] = reformatRecordData(componentAccuracyRecord[recordKey]);
            }
          }
        });
      });
    }
  }
  return {
    processedSummaryData,
    processedComponentData,
    allChannels,
    allComponents,
    sortedHeadings,
    sortedKeys,
    sortedMonths,
  };
};



// WEBPACK FOOTER //
// ./src/components/reports/utils/transformations.js